<template>
  <transition name="fade">
    <div
      v-if="shouldShowVisually"
      class="opacity-100 fixed top-0 bottom-0 right-0 left-0 bg-overlay-default z-overlay cursor-default"
      tabindex="0"
      role="button"
      aria-label="Stäng popup meny"
      @click="close"
      @keyup.esc.self.prevent="close"
      @keyup.space.self.prevent="close"
    ></div>
  </transition>

  <transition name="slide">
    <div
      v-show="shouldShowVisually"
      class="modal-wrapper z-modal fixed w-320 lg:w-auto"
    >
      <nav
        ref="focusTarget"
        aria-label="Meny"
        class="bg-surface-background lg:bg-none lg:bg-surface-primary lg:rounded-md w-320 lg:w-720 relative p-24 pt-0 top-2/4 left-2/4 -translate-x-1/2 -translate-y-1/2 max-h-full h-full lg:h-auto overflow-y-scroll"
      >
        <div
          class="bg-surface-primary border-b-md border-solid border-border-secondary pb-16 pt-24 flex flex-row justify-between items-center w-full sticky top-0"
        >
          <NuxtLink
            to="https://www.halebop.se/"
            external
            aria-label="Gå till Halebops startsida"
          >
            <NuxtImg
              src="/images/logos/halebop_header_logo.svg"
              alt="halebop logga"
              class="h-32 w-112 block dark:hidden"
              width="112"
              loading="lazy"
              provider="cloudfront"
            />
            <NuxtImg
              src="/images/logos/halebop_header_logo_dark_theme.svg"
              alt="halebop logga"
              class="h-32 w-112 hidden dark:block"
              width="112"
              loading="lazy"
              provider="cloudfront"
            />
          </NuxtLink>

          <IconButton
            aria-label="Stäng popup meny"
            icon-name="x"
            @click="close"
            @keyup.enter.self.prevent="close"
            @keyup.space.self.prevent="close"
            @keyup.esc="close"
          />
        </div>

        <HeaderMenuModalLinks
          :menu-is-shown-visually="shouldShowVisually ?? false"
        />
      </nav>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { watch } from 'vue';
import { onKeyStroke } from '@vueuse/core';

const { deactivateFocusTrap, focusTarget, activateFocusTrap } =
  useModalFocusTrap({ immediate: false });
const { activatePageScrollLock, deactivatePageScrollLock } =
  usePageScrollLockState('app_menu_modal');

const shouldShowVisually = defineModel<boolean>();
const emit = defineEmits(['onClose']);
const { pushNoMenyClickEvent } = useGoogleAnalyticsEvents();

const close = () => {
  shouldShowVisually.value = false;
  pushNoMenyClickEvent();
  emit('onClose');
};

function onOpen() {
  activatePageScrollLock();
  setTimeout(() => {
    activateFocusTrap();
  }, 300);
}

function onClose() {
  deactivateFocusTrap();
  deactivatePageScrollLock();
}

onKeyStroke(
  'Escape',
  () => {
    close();
  },
  { target: focusTarget },
);

onUnmounted(() => {
  deactivateFocusTrap();
});

watch(shouldShowVisually, (isOpen) => {
  if (isOpen) {
    onOpen();
  } else {
    onClose();
  }
});
</script>

<style scoped lang="scss">
.modal-wrapper {
  height: calc(100vh - 120px);
  left: 50%;
  max-width: none;
  min-width: 0;
  right: auto;
  top: 50%;
  transform: translate(-50%, -50%);
  outline: none;
  @include mq('lg') {
    height: 100%;
    left: auto;
    right: 0;
    top: 0;
    transform: none;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-in-out;
  @include mq('lg') {
    transition: right 0.3s ease-in-out;
  }
}

.slide-enter-from,
.slide-leave-to {
  top: 100%;
  @include mq('lg') {
    right: -30rem;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
  @include mq('lg') {
    transition: opacity 0.3s ease-in-out;
  }
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
    @include mq('lg') {
      transition: none;
    }
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: none;
    @include mq('lg') {
      transition: none;
    }
  }
}

/* width */
::-webkit-scrollbar {
  width: 12px;
}

/* Track */
::-webkit-scrollbar-track {
  box-shadow: none;
  border-radius: 8px;
  margin-top: 74px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  border-radius: 8px;

  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  border-radius: 9999px;
  background-color: #00000066;
}
</style>
